import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell, PieChart, Pie
} from 'recharts';
import './VMMetrics.css';

function fmtBps(bytes) {
  if (!bytes || bytes <= 0) return '0 B/s';
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB/s`;
  if (bytes >= 1024 * 1024)        return `${(bytes / (1024 * 1024)).toFixed(1)} MB/s`;
  if (bytes >= 1024)               return `${(bytes / 1024).toFixed(1)} KB/s`;
  return `${Math.round(bytes)} B/s`;
}

const TIMEFRAMES = [
  { value: 'hour',  label: 'Última hora' },
  { value: 'day',   label: 'Último dia'  },
  { value: 'week',  label: 'Última semana' },
  { value: 'month', label: 'Último mês' },
];

function VMMetrics({ vmid }) {
  const [metrics,   setMetrics]   = useState(null);
  const [rrdData,    setRrdData]    = useState([]);
  const [timeframe,  setTimeframe]  = useState('hour');
  const [loading,    setLoading]    = useState(true);
  const [rrdLoading, setRrdLoading] = useState(false);
  const [rrdError,   setRrdError]   = useState('');
  const [error,      setError]      = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Busca dados em tempo real (cards) — a cada 5 s
  useEffect(() => {
    fetchMetrics();
    if (!autoRefresh) return;
    const t = setInterval(fetchMetrics, 5000);
    return () => clearInterval(t);
  }, [vmid, autoRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

  // Busca histórico RRD ao montar e quando timeframe muda; atualiza a cada 60 s
  useEffect(() => {
    fetchRRD();
    const t = setInterval(fetchRRD, 60000);
    return () => clearInterval(t);
  }, [vmid, timeframe]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMetrics = async () => {
    try {
      const res = await axios.get(`/api/vms/${vmid}/metrics`);
      setMetrics(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao obter métricas');
    } finally {
      setLoading(false);
    }
  };

  const fetchRRD = async () => {
    setRrdLoading(true);
    setRrdError('');
    try {
      const res = await axios.get(`/api/vms/${vmid}/rrddata?timeframe=${timeframe}`);
      const pts = res.data.data || [];
      setRrdData(pts);
      if (pts.length === 0) {
        setRrdError('Proxmox não retornou dados históricos para este período.');
      }
    } catch (e) {
      setRrdError(e.response?.data?.error || 'Erro ao carregar dados históricos.');
    } finally {
      setRrdLoading(false);
    }
  };

  if (loading) return <div className="metrics-loading">Carregando métricas...</div>;
  if (error)   return <div className="metrics-error">{error}</div>;
  if (!metrics) return <div className="metrics-error">Nenhuma métrica disponível</div>;

  // Hotplug detection
  const rawHotplug = String(metrics.hotplug ?? '');
  const hotplugStr = rawHotplug === '1' ? 'network,disk,usb' : rawHotplug === '0' ? '' : rawHotplug;
  const hotplugItems = hotplugStr.split(',').map(s => s.trim());
  const cpuHotplug    = hotplugItems.includes('cpu');
  const memoryHotplug = hotplugItems.includes('memory');

  // OS compatibility for hotplug
  const ostype = metrics.ostype || '';
  const isLinux      = ostype === 'l26' || ostype === 'l24';
  const isWinServer  = ['win2k8', 'win2k12', 'win2k16', 'win2k19', 'win2k22'].includes(ostype);
  const isWinDesktop = ['wxp', 'w2k', 'win7', 'win8', 'win10', 'win11'].includes(ostype);

  const cpuHotplugCompat = isLinux ? 'supported' : isWinServer ? 'partial' : isWinDesktop ? 'unsupported' : ostype ? 'unknown' : 'unknown';
  const memHotplugCompat = isLinux ? 'supported' : isWinServer ? 'partial' : isWinDesktop ? 'unsupported' : ostype ? 'unknown' : 'unknown';

  const hotplugCompatLabel = {
    supported:   'SO compatível',
    partial:     'Suporte parcial (Windows Server)',
    unsupported: 'SO não suporta hot-plug',
    unknown:     'Compatibilidade desconhecida',
  };

  const cpuPercent    = (metrics.cpu.used * 100).toFixed(2);
  const memoryGB      = (metrics.memory.used / (1024 * 1024 * 1024)).toFixed(2);
  const memoryPercent = ((metrics.memory.used / (metrics.memory.total * 1024 * 1024)) * 100).toFixed(2);

  const uptimeFormatted = (() => {
    const s = metrics.uptime;
    if (!s) return '0h';
    const years  = Math.floor(s / 31536000);
    const months = Math.floor((s % 31536000) / 2592000);
    const days   = Math.floor((s % 2592000)  / 86400);
    const hours  = Math.floor((s % 86400)    / 3600);
    const parts = [];
    if (years)  parts.push(`${years}a`);
    if (months) parts.push(`${months}m`);
    if (days)   parts.push(`${days}d`);
    if (hours || parts.length === 0) parts.push(`${hours}h`);
    return parts.join(' ');
  })();

  const cpuData    = [{ name: 'Usado', value: parseFloat(cpuPercent) }, { name: 'Livre', value: 100 - parseFloat(cpuPercent) }];
  const memoryData = [{ name: 'Usado', value: parseFloat(memoryPercent) }, { name: 'Livre', value: 100 - parseFloat(memoryPercent) }];
  const PIE_COLORS = ['#10b981', '#334155'];

  const tooltipStyle = { backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '0.375rem' };
  const labelStyle   = { color: '#94a3b8' };
  const gridColor    = '#334155';

  // Domínio do eixo Y para disco e rede (máx do conjunto, mínimo 1 KB/s para não achatar)
  const validRrd = rrdData.filter(d => d.diskRead != null || d.netIn != null);
  const maxDisk  = Math.max(...validRrd.map(d => Math.max(d.diskRead || 0, d.diskWrite || 0)), 1024);
  const maxNet   = Math.max(...validRrd.map(d => Math.max(d.netIn    || 0, d.netOut    || 0)), 1024);

  // Quantidade de ticks no eixo X — reduz conforme densidade
  const tickInterval = rrdData.length > 50 ? Math.floor(rrdData.length / 10) : 'preserveStartEnd';

  return (
    <div className="metrics-container">
      <div className="metrics-header">
        <h2>📊 Métricas da VM - {metrics?.name || vmid}</h2>
        <div className="metrics-controls">
          <label className="auto-refresh-toggle">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-atualizar (5s)
          </label>
          <select
            className="timeframe-select"
            value={timeframe}
            onChange={e => setTimeframe(e.target.value)}
            title="Período dos gráficos"
          >
            {TIMEFRAMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <button className="metrics-refresh-btn" onClick={() => { fetchMetrics(); fetchRRD(); }}>
            🔄 Atualizar
          </button>
        </div>
      </div>

      {/* Linha 1: Status, Uptime, CPU, Memória */}
      <div className="metrics-cards">
        <div className="metric-card">
          <div className="metric-label">Status</div>
          <div className="metric-value status">
            {metrics.status === 'running' ? '✅ Em execução' : '⏹️ Parada'}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Uptime</div>
          <div className="metric-value">{uptimeFormatted}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">
            CPU — {metrics.cpu.cores} cores
            {cpuHotplug && (
              <span
                className={`hotplug-badge hotplug-${cpuHotplugCompat}`}
                title={`Hot-plug CPU ativo — ${hotplugCompatLabel[cpuHotplugCompat]}`}
              >
                ⚡ HP
              </span>
            )}
          </div>
          <div className="metric-value">{cpuPercent}%</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">
            Memória — {metrics.memory.total >= 1024 ? (metrics.memory.total / 1024).toFixed(0) + ' GB' : metrics.memory.total + ' MB'}
            {memoryHotplug && (
              <span
                className={`hotplug-badge hotplug-${memHotplugCompat}`}
                title={`Hot-plug Memória ativo — ${hotplugCompatLabel[memHotplugCompat]}`}
              >
                ⚡ HP
              </span>
            )}
          </div>
          <div className="metric-value">{memoryPercent}%</div>
        </div>
      </div>

      {/* Linha 2: Disco e Rede (I/O do RRD) */}
      {validRrd.length > 0 && (() => {
        const last = validRrd[validRrd.length - 1];
        return (
          <div className="metrics-cards metrics-cards-io">
            <div className="metric-card">
              <div className="metric-label">Disco Leitura</div>
              <div className="metric-value metric-io">{fmtBps(last.diskRead)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Disco Escrita</div>
              <div className="metric-value metric-io">{fmtBps(last.diskWrite)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Rede Entrada</div>
              <div className="metric-value metric-io">{fmtBps(last.netIn)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Rede Saída</div>
              <div className="metric-value metric-io">{fmtBps(last.netOut)}</div>
            </div>
          </div>
        );
      })()}

      {rrdLoading && <div className="rrd-loading">Atualizando gráficos históricos...</div>}
      {rrdError   && <div className="rrd-error">{rrdError}</div>}

      {/* Gráficos série temporal (RRD) */}
      <div className="charts-grid">

        {/* CPU */}
        <div className="chart-container">
          <h3>CPU</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={rrdData} connectNulls={false}>
              <defs>
                <linearGradient id="gCpu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={tickInterval} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} width={40} />
              <Tooltip formatter={v => v != null ? `${v}%` : '—'} contentStyle={tooltipStyle} labelStyle={labelStyle} />
              <Area type="monotone" dataKey="cpu" name="CPU" stroke="#3b82f6" fill="url(#gCpu)" connectNulls={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Memória */}
        <div className="chart-container">
          <h3>Memória</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={rrdData} connectNulls={false}>
              <defs>
                <linearGradient id="gMem" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={tickInterval} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} width={40} />
              <Tooltip formatter={v => v != null ? `${v}%` : '—'} contentStyle={tooltipStyle} labelStyle={labelStyle} />
              <Area type="monotone" dataKey="mem" name="Memória" stroke="#8b5cf6" fill="url(#gMem)" connectNulls={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Disco I/O */}
        <div className="chart-container">
          <h3>Disco I/O</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={rrdData} connectNulls={false}>
              <defs>
                <linearGradient id="gDR" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#10b981" stopOpacity={0.7}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
                </linearGradient>
                <linearGradient id="gDW" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#f59e0b" stopOpacity={0.7}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={tickInterval} />
              <YAxis domain={[0, maxDisk * 1.2]} tick={{ fontSize: 10 }} tickFormatter={v => fmtBps(v).replace('/s','')} width={70} />
              <Tooltip formatter={v => v != null ? fmtBps(v) : '—'} contentStyle={tooltipStyle} labelStyle={labelStyle} />
              <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
              <Area type="monotone" dataKey="diskRead"  name="Leitura" stroke="#10b981" fill="url(#gDR)" connectNulls={false} />
              <Area type="monotone" dataKey="diskWrite" name="Escrita" stroke="#f59e0b" fill="url(#gDW)" connectNulls={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Rede */}
        <div className="chart-container">
          <h3>Rede</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={rrdData} connectNulls={false}>
              <defs>
                <linearGradient id="gNI" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#06b6d4" stopOpacity={0.7}/>
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.05}/>
                </linearGradient>
                <linearGradient id="gNO" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#f43f5e" stopOpacity={0.7}/>
                  <stop offset="95%" stopColor="#f43f5e" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={tickInterval} />
              <YAxis domain={[0, maxNet * 1.2]} tick={{ fontSize: 10 }} tickFormatter={v => fmtBps(v).replace('/s','')} width={70} />
              <Tooltip formatter={v => v != null ? fmtBps(v) : '—'} contentStyle={tooltipStyle} labelStyle={labelStyle} />
              <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
              <Area type="monotone" dataKey="netIn"  name="Entrada" stroke="#06b6d4" fill="url(#gNI)" connectNulls={false} />
              <Area type="monotone" dataKey="netOut" name="Saída"   stroke="#f43f5e" fill="url(#gNO)" connectNulls={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* Pizzas + info técnica */}
      <div className="charts-bottom-row">
        <div className="chart-container">
          <h3>Distribuição CPU</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={cpuData} cx="50%" cy="50%" innerRadius={40} outerRadius={75} paddingAngle={5} dataKey="value">
                {cpuData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
              </Pie>
              <Tooltip formatter={v => `${v}%`} contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3>Distribuição Memória</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={memoryData} cx="50%" cy="50%" innerRadius={40} outerRadius={75} paddingAngle={5} dataKey="value">
                {memoryData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
              </Pie>
              <Tooltip formatter={v => `${v}%`} contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="metrics-info">
          <h3>Informações Técnicas</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">ID da VM</span>
              <span className="info-value">{metrics.vmid}</span>
            </div>
            <div className="info-item">
              <span className="info-label">CPU</span>
              <span className="info-value">
                {metrics.cpu.cores} vCPUs{metrics.cpu.sockets > 1 ? ` (${metrics.cpu.sockets}s × ${metrics.cpu.coresPerSocket}c)` : ''}
                {cpuHotplug && (
                  <span className={`hotplug-badge hotplug-${cpuHotplugCompat}`} title={`Hot-plug CPU — ${hotplugCompatLabel[cpuHotplugCompat]}`}>⚡ HP</span>
                )}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Memória Total</span>
              <span className="info-value">
                {metrics.memory.total} MB
                {memoryHotplug && (
                  <span className={`hotplug-badge hotplug-${memHotplugCompat}`} title={`Hot-plug Memória — ${hotplugCompatLabel[memHotplugCompat]}`}>⚡ HP</span>
                )}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Uptime</span>
              <span className="info-value">{uptimeFormatted}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Atualizado</span>
              <span className="info-value">{new Date(metrics.timestamp).toLocaleTimeString('pt-BR')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VMMetrics;
